import { BALL_RADIUS, POCKET_RADIUS, POCKET_POSITIONS, TABLE_WIDTH } from '../config/PhysicsConfig';

export interface PocketEvent {
  ballId: string;
  pocketName: string;
  timestamp: number;
}

/**
 * Pocket names for identification
 */
export type PocketName = 'back_left' | 'back_center' | 'back_right' | 'side_left' | 'side_right';

/**
 * PocketDetector handles detection of balls entering pockets
 */
export class PocketDetector {
  private pocketedBalls: Set<string> = new Set();
  private pocketCallbacks: ((event: PocketEvent) => void)[] = [];
  private pocketRadius: number;
  private ballRadius: number;

  private pockets = [
    { name: 'back_left' as PocketName, ...POCKET_POSITIONS.BACK_LEFT },
    { name: 'back_center' as PocketName, ...POCKET_POSITIONS.BACK_CENTER },
    { name: 'back_right' as PocketName, ...POCKET_POSITIONS.BACK_RIGHT },
    { name: 'side_left' as PocketName, ...POCKET_POSITIONS.SIDE_LEFT },
    { name: 'side_right' as PocketName, ...POCKET_POSITIONS.SIDE_RIGHT },
  ];

  constructor(pocketRadius: number = POCKET_RADIUS, ballRadius: number = BALL_RADIUS) {
    this.pocketRadius = pocketRadius;
    this.ballRadius = ballRadius;
  }

  /**
   * Check if a ball has entered any pocket.
   *
   * Side pockets sit exactly on the table edge, so the circular distance check
   * can miss fast-moving balls that tunnel past the detection zone between frames.
   * For side pockets we use a wall-proximity + y-range check instead: the ball is
   * considered pocketed as soon as its edge reaches the wall (centre within
   * ballRadius of wall x) while its y is within pocketRadius of the pocket centre.
   * This fires on the approach frame, before the ball can tunnel past.
   *
   * When prevPosition is supplied, an additional swept-segment check runs for side
   * pockets: if the ball's linear path between frames crosses the detection threshold
   * at a y within the pocket opening, it is captured even if the start and end
   * positions are both outside the y-window (tunneling at low frame rates).
   *
   * Corner/back pockets require deeper entry than first contact to avoid
   * unrealistic "near-miss" captures at the rim.
   */
  checkBallInPocket(
    ballId: string,
    position: { x: number; y: number },
    prevPosition?: { x: number; y: number },
  ): PocketName | null {
    // Skip already pocketed balls
    if (this.pocketedBalls.has(ballId)) return null;

    for (const pocket of this.pockets) {
      let detected = false;

      if (pocket.name === 'side_left' || pocket.name === 'side_right') {
        // Wall-proximity detection for side pockets.
        // Fire when ball edge is at or past the wall AND y is within pocket opening.
        const wallX = pocket.x; // ±TABLE_WIDTH/2
        const yInRange = Math.abs(position.y - pocket.y) <= this.pocketRadius;
        const atWall = pocket.name === 'side_left'
          ? position.x <= wallX + this.ballRadius   // approaching/past left wall
          : position.x >= wallX - this.ballRadius;  // approaching/past right wall
        detected = yInRange && atWall;

        // Swept-segment check: ball may have crossed the threshold mid-frame with its
        // y inside the pocket window even though the endpoint y is outside it.
        if (!detected && prevPosition) {
          detected = this.sweptSidePocketCheck(pocket, position, prevPosition);
        }
      } else {
        // Require about half-ball radial overlap before capture.
        const captureRadius = this.pocketRadius + this.ballRadius * 0.5;
        const distance = Math.sqrt(
          Math.pow(position.x - pocket.x, 2) + Math.pow(position.y - pocket.y, 2)
        );
        detected = distance <= captureRadius;
      }

      if (detected) {
        this.pocketedBalls.add(ballId);

        const event: PocketEvent = {
          ballId,
          pocketName: pocket.name,
          timestamp: Date.now(),
        };

        // Notify listeners
        for (const callback of this.pocketCallbacks) {
          callback(event);
        }

        return pocket.name;
      }
    }

    return null;
  }

  /**
   * Parametric line-segment vs. x-threshold intersection for a side pocket.
   * Returns true if the ball's straight-line path from prevPosition to position
   * crosses the detection threshold at a y within the pocket opening.
   */
  private sweptSidePocketCheck(
    pocket: { name: PocketName; x: number; y: number },
    position: { x: number; y: number },
    prevPosition: { x: number; y: number },
  ): boolean {
    const dx = position.x - prevPosition.x;
    if (dx === 0) return false; // moving parallel to wall — no crossing possible

    const threshold = pocket.name === 'side_left'
      ? pocket.x + this.ballRadius   // left detection edge
      : pocket.x - this.ballRadius;  // right detection edge

    const t = (threshold - prevPosition.x) / dx;
    if (t < 0 || t > 1) return false; // crossing outside this frame's segment

    const yCross = prevPosition.y + t * (position.y - prevPosition.y);
    return Math.abs(yCross - pocket.y) <= this.pocketRadius;
  }

  /**
   * Check if ball has entered gutter zone
   * Gutters exist in lower half of table
   */
  checkBallInGutter(position: { x: number; y: number }): boolean {
    // Gutter detection: catch balls at or beyond the table edge
    // Reduced threshold to catch balls sitting on rails (from 0.1m to 0.03m)
    const gutterThreshold = 0.03; // 3cm past table edge (or AT the edge)
    const sidePocketY = POCKET_POSITIONS.SIDE_LEFT.y;
    // Gutters extend halfway between starting area and side pockets
    const gutterEndY = sidePocketY / 2; // ~1.9m from bottom

    // Check if ball is at or beyond table edge in gutter zone (lower section)
    if (position.y < gutterEndY) {
      const isInLeftGutter = position.x < -TABLE_WIDTH / 2 - gutterThreshold;
      const isInRightGutter = position.x > TABLE_WIDTH / 2 + gutterThreshold;
      return isInLeftGutter || isInRightGutter;
    }

    return false;
  }

  /**
   * Register callback for pocket events
   */
  onPocket(callback: (event: PocketEvent) => void): void {
    this.pocketCallbacks.push(callback);
  }

  /**
   * Check if ball has been pocketed
   */
  isBallPocketed(ballId: string): boolean {
    return this.pocketedBalls.has(ballId);
  }

  /**
   * Get all pocketed ball IDs
   */
  getPocketedBalls(): string[] {
    return Array.from(this.pocketedBalls);
  }

  /**
   * Reset pocket tracking
   */
  reset(): void {
    this.pocketedBalls.clear();
  }

  /**
   * Get pocket positions for rendering
   */
  getPocketPositions() {
    return this.pockets;
  }
}
